import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, XCircle, Download, Plus, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import {
  BackLink,
  HeroStatus,
  Section,
  Card,
  CardTitle,
  ProgressCard,
  KvGrid,
  KvItem,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  PdfCard,
  fmtItalianDateTime,
  timeAgo,
  useNow,
  upperOrDash,
  EM_DASH,
} from "./Primitives";

const VISURA_TYPE_LABELS = {
  visura_ord_societa_capitali: "Ordinaria, Società di capitali",
  visura_ord_societa_persone: "Ordinaria, Società di persone",
  visura_ord_impresa_individuale: "Ordinaria, Impresa individuale",
};

const upperFirst = (s) => (s && typeof s === "string" ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const ResultVisuraCamerale = ({ request: initial, onUpdate, onNewSearch }) => {
  const navigate = useNavigate();
  const [request, setRequest] = useState(initial);
  useEffect(() => setRequest(initial), [initial]);

  useNow(10000);

  // Polling status auto ogni 10s se pending
  useEffect(() => {
    const isPending = request?.status === "pending_external" || request?.status === "in_progress";
    if (!isPending || !request?.id || String(request.id).startsWith("preview-")) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/requests/${request.id}`);
        setRequest(data);
        if (onUpdate) onUpdate(data);
      } catch (e) { /* swallow */ }
    }, 10000);
    return () => clearInterval(t);
  }, [request?.id, request?.status, onUpdate]);

  const onManualRefresh = async () => {
    if (!request?.id || String(request.id).startsWith("preview-")) return;
    try {
      const { data } = await api.get(`/requests/${request.id}`);
      setRequest(data);
      if (onUpdate) onUpdate(data);
    } catch (e) { /* swallow */ }
  };

  const piva = request?.params?.cf_piva_id || EM_DASH;
  const tipologiaLabel = VISURA_TYPE_LABELS[request?.service_id] || request?.service_name || EM_DASH;
  const subjectName = request?.response_data?.denominazione || request?.response_data?.ragione_sociale;

  /* ----------------------- IN-PROGRESS ----------------------- */
  if (request?.status === "pending_external" || request?.status === "in_progress" || request?.status === "pending_confirmation") {
    const since = timeAgo(request?.created_at);
    return (
      <div className="max-w-3xl" data-testid="result-visura-in-progress">
        <BackLink to="/history" label="Storico richieste" />

        <HeroStatus
          variant="info"
          title="Visura in lavorazione"
          description="La Camera di Commercio sta elaborando la richiesta · controlliamo ogni 30 secondi"
          testId="result-hero"
        />

        <Section>
          <ProgressCard
            title="Attesa risposta dalla Camera di Commercio"
            description={`Richiesta inviata ${since}. Tempo medio di elaborazione: 1–3 minuti.`}
          />
        </Section>

        <Section>
          <Card>
            <CardTitle>Dati richiesta</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="Soggetto" value={`P.IVA ${upperOrDash(piva)}`} mono />
              <KvItem label="Tipologia" value={tipologiaLabel} />
              <KvItem
                label="Stato"
                value={
                  <span className="text-warning-strong font-medium">
                    {request?.openapi_state ? upperFirst(request.openapi_state) : "In erogazione"}
                  </span>
                }
              />
              <KvItem label="Richiesta inviata" value={fmtItalianDateTime(request?.created_at)} />
            </KvGrid>
          </Card>
        </Section>

        <FooterActions>
          <ActionButton variant="outline" icon={RefreshCw} onClick={onManualRefresh} testId="action-refresh">
            Aggiorna stato
          </ActionButton>
          <ActionButton variant="ghost" icon={XCircle} onClick={() => {}} testId="action-cancel">
            Annulla richiesta
          </ActionButton>
        </FooterActions>

        <TechDetailsToggle data={request} />
      </div>
    );
  }

  /* ----------------------- COMPLETED ----------------------- */
  if (request?.status === "completed") {
    const createdAt = request?.created_at;
    const completedAt = request?.completed_at || createdAt;
    const elapsedSec = createdAt && completedAt
      ? Math.max(1, Math.round((new Date(completedAt).getTime() - new Date(createdAt).getTime()) / 1000))
      : null;
    const sinceTxt = elapsedSec
      ? elapsedSec < 60
        ? `${elapsedSec} secondi`
        : `${Math.round(elapsedSec / 60)} minuti`
      : null;

    const onDownload = async () => {
      if (!request?.id) return;
      if (String(request.id).startsWith("preview-")) {
        toast.info("Anteprima: download non disponibile sui mock");
        return;
      }
      try {
        const token = localStorage.getItem("wattline_token");
        const res = await fetch(`${API}/requests/${request.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { toast.error("Errore download PDF"); return; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = request.pdf_filename || `visura_${request.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        toast.error("Errore download PDF");
      }
    };

    const onPreview = onDownload; // browser apre il PDF se mime corretto - per ora identici

    const onExportJson = () => {
      try {
        const blob = new Blob([JSON.stringify(request, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `visura_${request?.id || "result"}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } catch { toast.error("Errore export"); }
    };

    return (
      <div className="max-w-3xl" data-testid="result-visura-completed">
        <BackLink to="/history" label="Storico richieste" />

        <HeroStatus
          variant="success"
          title="Visura disponibile"
          description={`Documento ufficiale Camera di Commercio${sinceTxt ? ` · pronto in ${sinceTxt}` : ""}`}
          testId="result-hero"
        />

        <Section>
          <PdfCard
            title={`Visura ordinaria — ${tipologiaLabel.replace(/^Ordinaria,\s*/, "")}`}
            subject={subjectName || `P.IVA ${upperOrDash(piva)}`}
            sizeKB={request?.pdf_size_bytes ? Math.round(request.pdf_size_bytes / 1024) : null}
            generatedAt={fmtItalianDateTime(completedAt)}
            onDownload={onDownload}
            onPreview={onPreview}
          />
        </Section>

        <Section>
          <Card>
            <CardTitle>Dati richiesta</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="Soggetto" value={subjectName || `P.IVA ${upperOrDash(piva)}`} testId="kv-subject" />
              <KvItem label="P.IVA / C.F." value={upperOrDash(piva)} mono testId="kv-piva" />
              <KvItem label="Tipologia" value={tipologiaLabel} testId="kv-tipologia" />
              <KvItem
                label="Stato richiesta"
                value={
                  <span className="text-success font-medium">
                    {request?.openapi_state ? upperFirst(request.openapi_state) : "Evasa"}
                  </span>
                }
                testId="kv-stato"
              />
              <KvItem label="Richiesta inviata" value={fmtItalianDateTime(createdAt)} testId="kv-created" />
              <KvItem label="PDF generato" value={fmtItalianDateTime(completedAt)} testId="kv-completed" />
            </KvGrid>
          </Card>
        </Section>

        <FooterActions>
          <ActionButton variant="primary" icon={Plus} onClick={() => (onNewSearch ? onNewSearch() : navigate("/search/visure"))} testId="action-new">
            Nuova visura
          </ActionButton>
          <ActionButton variant="outline" icon={Download} onClick={onExportJson} testId="action-export-json">
            Esporta JSON
          </ActionButton>
        </FooterActions>

        <TechDetailsToggle data={request} />
      </div>
    );
  }

  /* ----------------------- FAILED ----------------------- */
  return (
    <div className="max-w-3xl" data-testid="result-visura-failed">
      <BackLink to="/history" label="Storico richieste" />
      <HeroStatus
        variant="danger"
        title="Richiesta fallita"
        description={request?.error_message || "Errore non specificato"}
      />
      <Section>
        <Card>
          <CardTitle>Dati richiesta</CardTitle>
          <KvGrid columns={2}>
            <KvItem label="Soggetto" value={`P.IVA ${upperOrDash(piva)}`} mono />
            <KvItem label="Tipologia" value={tipologiaLabel} />
            <KvItem label="Stato esterno" value={request?.openapi_state || EM_DASH} />
            <KvItem label="Errore" value={request?.error_message || EM_DASH} />
          </KvGrid>
        </Card>
      </Section>
      <TechDetailsToggle data={request} />
    </div>
  );
};
